import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { MdOutlineCall, MdApproval, MdLocationOn, MdMic } from 'react-icons/md';
import { BiPlus } from 'react-icons/bi';
import SidebarItem from './SidebarItem';
import { IoChevronDownSharp } from 'react-icons/io5';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAgent } from 'providers/context/AgentProvider';
import Spinner from 'components/Common/Spinner';
import { useGetAgents } from 'apis/business/agent/hook';
import * as urls from 'routes/config';
import { WEB_EMBED_SCRIPT } from 'env';
import PerformanceIcon from 'assets/icons/PerformanceIcon';
import CallLogsIcon from 'assets/icons/CallLogsIcon';
import ConfigureIcon from 'assets/icons/ConfigureIcon';
import { CacheKeys } from 'utils/cacheKeys';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { queryClient } from 'providers/AllProviders';
import { deleteAgent } from 'apis/business/agent/api';
import WorksheetIcon from 'assets/icons/Worksheet';
import { FaPlus } from 'react-icons/fa6';
import { TalksRequest } from 'apis/business/agent/talks/types';
import { useForm } from 'react-hook-form';
import Button from 'components/Common/Button';
import TelInput from 'components/Common/TelInput';
import Input from 'components/Common/Input';
import { a11yProps } from 'components/Common/CustomTabPanel';
import { useTalks } from 'apis/business/agent/talks/hook';

const COLLAPSED_WIDTH = 80;
const EXPANDED_WIDTH = 240;

interface Props {
  isMenuOpen?: boolean;
  handleClose?: () => void;
}

export default function SidebarDynamic({ isMenuOpen, handleClose }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { workspaceId, agentId } = useParams();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const { selectedAgent, setSelectedAgent, setSelectedAgentId } = useAgent();
  const { data: agents, isLoading } = useGetAgents();
  const [value, setValue] = useState<number>(0);
  const [isTalkModalOpen, setIsTalkModalOpen] = React.useState(false);
  const { control, handleSubmit, reset } = useForm<TalksRequest>();
  const { mutateAsync: createTalks, isPending } = useTalks();

  const open = Boolean(anchorEl);

  useEffect(() => {
    const filteredAgent = agents?.data?.find((item) => item.id === Number(agentId));
    const agentIndex = agents?.data?.findIndex((item) => item.id === Number(agentId));

    setSelectedIndex(agentIndex !== -1 ? Number(agentIndex) : 0);
    setSelectedAgent(filteredAgent ?? agents?.data[0]);
  }, [agents, setSelectedAgent, setSelectedIndex, agentId]);

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (_: React.MouseEvent<HTMLElement>, index: number) => {
    setSelectedAgent(agents?.data?.[index]);
    setSelectedAgentId(agents?.data?.[index]?.id);
    localStorage.setItem(CacheKeys.selectedAgentId, String(agents?.data?.[index]?.id));
    setSelectedIndex(index);
    navigate(`${urls.agentBase(workspaceId, agents?.data?.[index]?.id)}/${urls.home.configure}`);
    queryClient.resetQueries();
    setAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCloseTalkModal = () => {
    reset();
    setIsTalkModalOpen(false);
  };

  const onSubmit = async (data: TalksRequest) => {
    await createTalks(data);
    handleCloseTalkModal();
  };

  useEffect(() => {
    const existingScript = document.querySelector(`script[src="${WEB_EMBED_SCRIPT}"]`);
    if (!existingScript) {
      const script1 = document.createElement('script');
      script1.src = WEB_EMBED_SCRIPT ?? '';
      script1.id = 'embed-script';
      script1.async = true;

      document.head.appendChild(script1);

      return () => {
        document.head.removeChild(script1);
      };
    }
  }, []);

  const handleTalk = async () => {
    const botRoot = document.getElementById('kickCall-chatbot-root');
    if (botRoot) botRoot.remove();
    (window as any).VoiceBot?.open(selectedAgent?.agent_token, true);
  };

  const handleDeleteAgent = async (id: number) => {
    const response = await deleteAgent(workspaceId!, id);
    if ((response as any)?.meta?.message) {
      queryClient.invalidateQueries({
        queryKey: ['agents', workspaceId],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['agent', workspaceId, id],
        refetchType: 'active'
      });
      const leftAgents = agents?.data.filter((item) => item.id !== id);
      if (leftAgents?.length) {
        setSelectedAgent(leftAgents?.[0]);
        setSelectedAgentId(leftAgents?.[0]?.id);
        localStorage.setItem(CacheKeys.selectedAgentId, String(leftAgents?.[0]?.id));
        setSelectedIndex(0);
        navigate(`${urls.agentBase(workspaceId, leftAgents?.[0]?.id)}/${urls.home.configure}`);
      } else {
        setSelectedAgent(undefined);
        setSelectedAgentId(undefined);
        localStorage.removeItem(CacheKeys.selectedAgentId);
        localStorage.removeItem(CacheKeys.agentIds);
        navigate(`${urls.base(workspaceId)}/${urls.onBoarding.create}`);
      }
    }
  };

  const drawerContent = (
    <List sx={{ paddingTop: 2 }}>
      <Box marginX={2}>
        <Box>
          <List component='nav' aria-label='Agent settings'>
            <ListItemButton
              id='agent-button'
              aria-haspopup='listbox'
              aria-controls='agent-menu'
              aria-label='agent selection'
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClickListItem}
              sx={{
                background: isHovered ? '' : '#E3E4E5',
                border: isHovered ? '1px solid #ccc' : '',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: isHovered ? 'flex-start' : 'center',
                padding: isHovered ? '8px 16px' : '8px 8px',
                transition: 'padding 0.3s, align-items 0.3s'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <ListItemText
                  primary={
                    <Typography
                      variant={isHovered ? 'h6' : 'body1'}
                      component='p'
                      color={isHovered ? '' : '#6D6D6D'}
                      fontWeight='600'
                      textAlign={isHovered ? 'left' : 'center'}
                      textTransform={isHovered ? 'capitalize' : 'uppercase'}
                      noWrap
                      title={agents?.data?.[selectedIndex]?.name}
                    >
                      {isHovered
                        ? agents?.data?.[selectedIndex]?.name
                        : agents?.data?.[selectedIndex]?.name.slice(0, 2)}
                    </Typography>
                  }
                  sx={{ margin: 0, padding: 0 }}
                />
                {isHovered && <IoChevronDownSharp />}
              </Box>
              {isHovered && (
                <>
                  <Box color={theme.palette.secondary.main} display='flex' alignItems='center' gap='4px' mt={1}>
                    <MdApproval />
                    <Typography variant='body2' color={theme.palette.secondary.main} textTransform='capitalize'>
                      {agents?.data?.[selectedIndex]?.agent_interaction}
                    </Typography>
                  </Box>
                  <Box color={theme.palette.secondary.main} display='flex' alignItems='center' gap='4px' mt={0.5}>
                    <MdLocationOn />
                    <Tooltip placement='top' title={agents?.data?.[selectedIndex]?.business_address}>
                      <Typography
                        variant='body2'
                        color={theme.palette.secondary.main}
                        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '153px' }}
                      >
                        {agents?.data?.[selectedIndex]?.business_address ?? '---'}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box color={theme.palette.secondary.main} display='flex' alignItems='center' gap='4px' mt={0.5}>
                    <MdOutlineCall />
                    <Typography variant='body2' color={theme.palette.secondary.main}>
                      {agents?.data?.[selectedIndex]?.phone_number ?? '---'}
                    </Typography>
                  </Box>
                </>
              )}
            </ListItemButton>
          </List>
          <Menu
            id='agent-menu'
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': 'agent-button',
              role: 'listbox'
            }}
            slotProps={{ paper: { sx: { width: 205 } } }}
          >
            <Box
              display='flex'
              justifyContent='center'
              gap={0.5}
              alignItems='center'
              border='1px solid #F3F3F3'
              color={theme.palette.secondary.main}
              margin={1}
              paddingY={0.75}
              borderRadius={0.5}
              sx={{
                textDecoration: 'none'
              }}
              component={Link}
              to={`${urls.base(workspaceId)}/${urls.onBoarding.selectAgent}`}
              onClick={() => handleMenuClose()}
            >
              <BiPlus />
              <Typography>Add Agent</Typography>
            </Box>
            {agents?.data.map((option, index) => (
              <MenuItem
                key={option.id}
                selected={index === selectedIndex}
                onClick={(event) => handleMenuItemClick(event, index)}
              >
                <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
                  <Typography
                    sx={{
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis'
                    }}
                    title={option.name}
                  >
                    {option.name}
                  </Typography>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAgent(option?.id);
                    }}
                    size='small'
                  >
                    <RiDeleteBin6Line style={{ color: 'red', fontSize: 16 }} />
                  </IconButton>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
      {!isHovered && (
        <ListItem>
          <Button color='secondary' variant='outlined' sx={{ borderRadius: 1, minWidth: 'unset' }}>
            <FaPlus />
          </Button>
        </ListItem>
      )}
      {!isHovered && (
        <Typography sx={{ marginTop: '16px', marginBottom: '12px', borderTop: '1px solid #ECECEC', mx: '16px' }} />
      )}
      <ListItem>
        <Button
          fullWidth
          size='small'
          onClick={() => setIsTalkModalOpen(true)}
          sx={{
            justifyContent: 'center',
            transition: 'padding 0.3s, justify-content 0.3s',
            minWidth: 'unset'
          }}
        >
          <MdMic style={{ fontSize: 18, marginRight: isHovered ? 8 : 0 }} />
          {isHovered && <Typography>Talk</Typography>}
        </Button>
      </ListItem>
      <Modal
        open={isTalkModalOpen}
        onClose={() => handleCloseTalkModal()}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box sx={style}>
          <Typography variant='h6' component='h2' mb={1}>
            Talk To {selectedAgent?.name}
          </Typography>
          <Box bgcolor='background.paper' mt={0} mb={2} py={0.5}>
            <Tabs value={value} onChange={(_, newValue) => setValue(newValue)} aria-label='configure tabs'>
              <Tab sx={{ width: '50%' }} label='Phone' {...a11yProps('talk', 'talkpanel', 0)} />
              <Tab sx={{ width: '50%' }} label='Web' {...a11yProps('talk', 'talkpanel', 1)} />
            </Tabs>
          </Box>
          {value === 0 && (
            <Box component='form' onSubmit={handleSubmit(onSubmit)}>
              <Box>
                <Input
                  required
                  size='small'
                  name='name'
                  fullWidth
                  control={control}
                  label='Name'
                  placeholder='Enter your friendly name'
                />
                <TelInput
                  name='phone_number'
                  size='small'
                  sx={{ mt: 2 }}
                  label='Phone Number'
                  control={control}
                  placeholder='e.g. +1234567890'
                  defaultCountry='US'
                  preferredCountries={['US', 'CA', 'IN']}
                  fullWidth
                  required
                />
              </Box>
              <Button type='submit' isPending={isPending} sx={{ mt: 2 }} fullWidth>
                Call Me
              </Button>
            </Box>
          )}
          {value === 1 && (
            <Button
              sx={{ mt: 2 }}
              fullWidth
              onClick={() => {
                handleCloseTalkModal();
                handleTalk();
              }}
            >
              Start Call
            </Button>
          )}
        </Box>
      </Modal>
      <SidebarItem
        to={`${urls.agentBase(workspaceId, agentId)}/${urls.home.performance}`}
        icon={<PerformanceIcon />}
        label='Performance'
        isHovered={isHovered}
      />
      <SidebarItem
        to={`${urls.agentBase(workspaceId, agentId)}/${urls.home.callLogs}`}
        icon={<CallLogsIcon />}
        label='Call Logs'
        isHovered={isHovered}
      />
      <SidebarItem
        to={`${urls.worksheetBase(workspaceId, agentId)}`}
        icon={<WorksheetIcon />}
        label='Worksheet'
        isHovered={isHovered}
      />
      <SidebarItem
        to={
          agentId
            ? `${urls.agentBase(workspaceId, agentId)}/${urls.home.configure}`
            : `${urls.base(workspaceId)}/${urls.home.configure}`
        }
        icon={<ConfigureIcon />}
        label='Configure'
        isHovered={isHovered}
      />
    </List>
  );

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Drawer
      variant={isMediumScreen ? 'temporary' : 'permanent'}
      open={isMediumScreen ? isMenuOpen : true}
      onClose={handleClose}
      anchor='left'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        width: isHovered ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
        flexShrink: 0,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard
        }),
        '& .MuiDrawer-paper': {
          width: isHovered ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
          boxSizing: 'border-box',
          top: isMediumScreen ? 0 : '65px',
          backgroundColor: theme.palette.background.paper,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard
          }),
          overflowX: 'hidden'
        }
      }}
      ModalProps={{
        keepMounted: true
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  py: 4,
  px: 2
};
